import { localizedStrings } from '@core/strings'
import React, { FunctionComponent } from 'react'
import { Button, Tooltip } from '@common/material'
import { styled } from '@mui/material/styles'
import { Box, Flex, Image, Text } from '@fundamentals'
import { useTheme } from '@mui/material'
import {
  Close as CloseIcon,
  Edit as EditIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'

const SignatureCanvas =
  typeof window === 'undefined'
    ? null
    : require('react-signature-canvas').default

type ComponentType = {
  onChange?: () => void
  onEnd?: () => void
  onClear?: () => void
  readonly?: boolean
  sigRef: React.Ref<any>
  optional?: boolean
}

type ComponentTypeEdit = {
  onChange?: () => void
  onEnd?: () => void
  value?: string
  sigRef?: React.Ref<any>
  readonly?: boolean
  isEdit?: boolean
  setIsEdit?: (newVal: boolean) => void
  optional?: boolean
}
const Signature: FunctionComponent<ComponentType> = ({
  sigRef,
  onChange = () => {},
  onClear = () => {},
  onEnd = () => {},
  optional = false,
}) => {
  const theme: any = useTheme()

  return (
    <Box>
      <Flex
        sx={{
          mt: 3,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Flex sx={{ flex: 1, minWidth: 200 }} alignItems='center' gap={1}>
          <Text fontWeight='bold' component='label' sx={{ mb: 0 }}>
            {localizedStrings.digitalSignature}
            {optional ? ` (${localizedStrings.optional})` : ''}
          </Text>
          {!optional && (
            <Tooltip title={localizedStrings.digitalSignatureInfo}>
              <InfoIcon fontSize='small' color='action' />
            </Tooltip>
          )}
        </Flex>
        <Button
          variant='text'
          color='inherit'
          size='small'
          data-test='jsCancelEditSignature'
          startIcon={<CloseIcon />}
          onClick={() => {
            // @ts-ignore
            sigRef.current?.clear()
            onClear()
          }}
        >
          {localizedStrings.clear}
        </Button>
      </Flex>
      <StyledSignatureBox data-test='jsSignature'>
        <SignatureCanvas
          onBegin={onChange}
          onEnd={onEnd}
          ref={sigRef}
          penColor={theme.palette.primary.main}
        />
      </StyledSignatureBox>
    </Box>
  )
}

export default Signature

export const SignatureEdit: FunctionComponent<ComponentTypeEdit> = ({
  sigRef,
  readonly,
  value,
  isEdit,
  setIsEdit,
  onChange,
  onEnd,
  optional,
}) => {
  const theme: any = useTheme()

  return (
    <Box>
      <Flex sx={{ mt: 3 }} alignItems='center'>
        <Text fontWeight='bold' component='label' sx={{ mb: 0 }}>
          {localizedStrings.digitalSignature}{' '}
          {optional ? ' - ' + localizedStrings.optional : ''}
        </Text>
      </Flex>
      <StyledSignatureBox data-test='jsSignature'>
        {!isEdit ? (
          // We center the image to center legacy signatures (without strict 2:1 aspect ratio)
          <Box
            display='flex'
            width='100%'
            height='100%'
            alignItems='center'
            justifyContent='center'
          >
            <Image
              src={value}
              style={{ maxWidth: '100%', maxHeight: '100%', height: 'auto' }}
              data-test='signature-img'
            />
          </Box>
        ) : (
          <SignatureCanvas
            onBegin={onChange}
            ref={sigRef}
            penColor={theme.palette.primary.main}
            onEnd={onEnd}
          />
        )}
      </StyledSignatureBox>
      <Flex sx={{ justifyContent: 'flex-end' }}>
        {isEdit ? (
          <Button
            variant='text'
            color='inherit'
            size='small'
            data-test='jsCancelEditSignature'
            startIcon={<CloseIcon />}
            onClick={() => setIsEdit(false)}
          >
            {localizedStrings.cancel}
          </Button>
        ) : (
          !readonly && (
            <Button
              variant='text'
              color='inherit'
              size='small'
              data-test='jsEditSignature'
              startIcon={<EditIcon />}
              onClick={() => setIsEdit(true)}
            >
              {localizedStrings.edit}
            </Button>
          )
        )}
      </Flex>
    </Box>
  )
}

const StyledSignatureBox = styled(Box)`
  background: #f4f6fc;
  border: 1px solid #a4b5d2;
  border-radius: 6px;
  aspect-ratio: 2;
  overflow: hidden;
  box-sizing: border-box;
  max-height: 170px;

  canvas {
    width: 100%;
    height: 100%;
  }
`
